import { anyOf, arrayOf, maybe, objectOf } from "@altostra/type-validations";
import { boolean, nullValidation, number, string } from "@altostra/type-validations/lib/primitives";

import type { AgentPreviewAsyncResult, CampaignQaRuleStatus } from "./types";

/**
 * Every member of {@link CampaignQaRuleStatus}. Keyed by the union rather than typed as an
 * array of it so that adding a status without listing it here is a compile error — an array
 * of the union constrains its members but never requires all of them, which would let a new
 * status slip past the guard below and be silently rewritten to "unable_to_verify".
 * Mirrors `Braze::Agentic::QaAgentDefinition::QaResultStatuses`.
 */
const campaignQaRuleStatuses: Record<CampaignQaRuleStatus, true> = {
  pass: true,
  fail: true,
  unable_to_verify: true,
};

/**
 * Narrows an unvalidated value — a status read back out of a stored agent invocation
 * log, say — to a known status. Callers substitute "unable_to_verify" for anything else,
 * matching what the backend does when normalizing a completion it can't read.
 */
export const isCampaignQaRuleStatus = (value: unknown): value is CampaignQaRuleStatus =>
  typeof value === "string" && Object.hasOwn(campaignQaRuleStatuses, value);

const nullableString = anyOf(string, nullValidation);
const nullableArrayOfStrings = anyOf(arrayOf(string), nullValidation);

export const isAgentPreviewAsyncResult = objectOf<AgentPreviewAsyncResult>({
  status: number,
  result: maybe(nullableString),
  execution_time_ms: maybe(number),
  errors: maybe(nullableArrayOfStrings),
  error_code: maybe(string),
  tool_execution_logs: maybe(
    arrayOf(
      objectOf({
        name: string,
        arguments: maybe(nullableString),
        duration: maybe(number),
      })
    )
  ),
  execution_usage: maybe(
    objectOf({
      limits: objectOf({
        daily: maybe(number),
        max_daily: maybe(number),
        total: maybe(number),
      }),
      current_usage: objectOf({
        today: maybe(number),
        total: maybe(number),
      }),
      remaining: objectOf({
        today: maybe(number),
        total: maybe(number),
      }),
      exceeded_limit: boolean,
    })
  ),
  qa_results: maybe(
    anyOf(
      arrayOf(
        objectOf({
          rule: string,
          status: string,
          reason: string,
          ruleset_name: maybe(string),
        })
      ),
      nullValidation
    )
  ),
  chunked_result_complete: maybe(boolean),
});
